import { FC, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, XCircle, Calendar } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { DataTable, Column } from '../../components/admin/DataTable';
import { SearchInput } from '../../components/admin/SearchInput';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { FormModal } from '../../components/admin/FormModal';
import { adminApi } from '../../api/adminApi';
import { formatCurrencyAmount } from '../../utils/currency';
import type {
  AdminBooking,
  BookingListQuery,
  Pagination,
  AdminBookingStatus,
} from '../../types/admin.types';

type PageState = 'loading' | 'loaded' | 'error';
type ModalState = 'closed' | 'view' | 'cancel';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatDateTime(dateString: string): string {
  return `${formatDate(dateString)} at ${formatTime(dateString)}`;
}

const bookingStatuses: AdminBookingStatus[] = [
  'PENDING_PAYMENT',
  'CONFIRMED',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
];

export const BookingsPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [query, setQuery] = useState<BookingListQuery>({
    page: 1,
    limit: 10,
    status: (searchParams.get('status') as AdminBookingStatus) || undefined,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedBooking, setSelectedBooking] = useState<AdminBooking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      setPageState('loading');
      const response = await adminApi.listBookings(query);
      setBookings(response.data);
      setPagination(response.pagination);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load bookings');
      setPageState('error');
    }
  }, [query]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Sync URL params with query state
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (query.status) newParams.set('status', query.status);
    if (query.search) newParams.set('search', query.search);
    setSearchParams(newParams, { replace: true });
  }, [query.status, query.search, setSearchParams]);

  const handleSearch = (search: string) => {
    setQuery((prev) => ({ ...prev, search, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handleFilterChange = (key: keyof BookingListQuery, value: string) => {
    setQuery((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  };

  const handleDateFilterChange = (key: 'dateFrom' | 'dateTo', value: string) => {
    setQuery((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  };

  const openViewModal = (booking: AdminBooking) => {
    setSelectedBooking(booking);
    setModalState('view');
  };

  const openCancelModal = (booking: AdminBooking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    setModalState('cancel');
  };

  const closeModal = () => {
    setModalState('closed');
    setSelectedBooking(null);
    setCancelReason('');
  };

  const handleCancel = async () => {
    if (!selectedBooking) return;

    setIsSubmitting(true);
    try {
      await adminApi.cancelBooking(selectedBooking.id, { reason: cancelReason || undefined });
      setSuccessMessage('Booking cancelled successfully');
      closeModal();
      fetchBookings();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to cancel booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canCancelBooking = (booking: AdminBooking): boolean => {
    return booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED';
  };

  const columns: Column<AdminBooking>[] = [
    {
      key: 'startTime',
      header: 'Date & Time',
      render: (booking) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">{formatDate(booking.startTime)}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
          </p>
        </div>
      ),
    },
    {
      key: 'service.name',
      header: 'Service',
      render: (booking) => <p className="font-medium text-gray-900 dark:text-slate-100">{booking.service.name}</p>,
    },
    {
      key: 'user.name',
      header: 'Customer',
      render: (booking) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">{booking.user.name}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">{booking.user.email}</p>
        </div>
      ),
    },
    {
      key: 'therapist.user.name',
      header: 'Therapist',
      render: (booking) => booking.therapist.user.name,
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (booking) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">{formatCurrencyAmount(booking.totalAmount)}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">Paid: {formatCurrencyAmount(booking.paidAmount)}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (booking) => <StatusBadge status={booking.status} />,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Bookings</h2>
          <p className="mt-1 text-gray-500 dark:text-slate-400">Manage all bookings and appointments</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-green-800">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage('')}
              className="mt-1 text-sm text-green-600 hover:text-green-800"
            >
              Dismiss
            </button>
          </div>
        )}
        {errorMessage && pageState === 'loaded' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-800">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage('')}
              className="mt-1 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <SearchInput
            value={query.search || ''}
            onChange={handleSearch}
            placeholder="Search by customer name or email..."
            className="w-64"
          />
          <select
            value={query.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {bookingStatuses.map((status) => (
              <option key={status} value={status}>
                {status.replace('_', ' ')}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">From:</label>
            <input
              type="date"
              value={query.dateFrom || ''}
              onChange={(e) => handleDateFilterChange('dateFrom', e.target.value)}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">To:</label>
            <input
              type="date"
              value={query.dateTo || ''}
              onChange={(e) => handleDateFilterChange('dateTo', e.target.value)}
              className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {(query.status || query.dateFrom || query.dateTo || query.search) && (
            <button
              onClick={() => setQuery({ page: 1, limit: 10 })}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={bookings}
          keyExtractor={(b) => b.id}
          isLoading={pageState === 'loading'}
          emptyMessage="No bookings found"
          pagination={pagination || undefined}
          onPageChange={handlePageChange}
          onRowClick={openViewModal}
          actions={(booking) => (
            <>
              <button
                onClick={() => openViewModal(booking)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>
              {canCancelBooking(booking) && (
                <button
                  onClick={() => openCancelModal(booking)}
                  className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                  title="Cancel booking"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        />

        {/* View Modal */}
        {modalState === 'view' && selectedBooking && (
          <FormModal
            isOpen
            onClose={closeModal}
            onSubmit={closeModal}
            title="Booking Details"
            submitText="Close"
            cancelText=""
            size="lg"
          >
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Status</p>
                  <StatusBadge status={selectedBooking.status} />
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
              </div>

              {/* Date & Time */}
              <div className="rounded-lg bg-gray-50 dark:bg-slate-700 p-4">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Appointment</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  {formatDateTime(selectedBooking.startTime)}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Duration: {formatTime(selectedBooking.startTime)} -{' '}
                  {formatTime(selectedBooking.endTime)}
                </p>
              </div>

              {/* Service & Therapist */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Service</p>
                  <p className="text-gray-900 dark:text-slate-100">{selectedBooking.service.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Therapist</p>
                  <p className="text-gray-900 dark:text-slate-100">{selectedBooking.therapist.user.name}</p>
                </div>
              </div>

              {/* Customer */}
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Customer</p>
                <p className="text-gray-900 dark:text-slate-100">{selectedBooking.user.name}</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">{selectedBooking.user.email}</p>
                {selectedBooking.user.phone && (
                  <p className="text-sm text-gray-500 dark:text-slate-400">{selectedBooking.user.phone}</p>
                )}
              </div>

              {/* Payment */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Total Amount</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                    {formatCurrencyAmount(selectedBooking.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Paid Amount</p>
                  <p className="text-gray-900 dark:text-slate-100">{formatCurrencyAmount(selectedBooking.paidAmount)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Balance Due</p>
                  <p className="text-gray-900 dark:text-slate-100">
                    {formatCurrencyAmount(selectedBooking.totalAmount - selectedBooking.paidAmount)}
                  </p>
                </div>
              </div>

              {/* Payment Info */}
              {selectedBooking.payment && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Payment Status</p>
                  <StatusBadge status={selectedBooking.payment.status} />
                </div>
              )}

              {/* Notes */}
              {selectedBooking.customerNotes && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Customer Notes</p>
                  <p className="mt-1 rounded-lg bg-gray-50 dark:bg-slate-700 p-3 text-gray-900 dark:text-slate-100">
                    {selectedBooking.customerNotes}
                  </p>
                </div>
              )}

              {selectedBooking.therapistNotes && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Therapist Notes</p>
                  <p className="mt-1 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 p-3 text-gray-900 dark:text-slate-100">
                    {selectedBooking.therapistNotes}
                  </p>
                </div>
              )}

              {/* Cancellation Info */}
              {selectedBooking.status === 'CANCELLED' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-medium text-red-800">Cancellation Info</p>
                  {selectedBooking.cancelledAt && (
                    <p className="text-sm text-red-700">
                      Cancelled on: {formatDateTime(selectedBooking.cancelledAt)}
                    </p>
                  )}
                  {selectedBooking.cancellationReason && (
                    <p className="mt-1 text-sm text-red-700">
                      Reason: {selectedBooking.cancellationReason}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              {canCancelBooking(selectedBooking) && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => {
                      closeModal();
                      openCancelModal(selectedBooking);
                    }}
                    className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Booking
                  </button>
                </div>
              )}
            </div>
          </FormModal>
        )}

        {/* Cancel Confirmation Modal */}
        {modalState === 'cancel' && selectedBooking && (
          <ConfirmModal
            isOpen
            onClose={closeModal}
            onConfirm={handleCancel}
            title="Cancel Booking"
            message={`Are you sure you want to cancel the booking for ${selectedBooking.user.name} on ${formatDate(selectedBooking.startTime)}?`}
            confirmText="Cancel Booking"
            variant="danger"
            isLoading={isSubmitting}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default BookingsPage;
